import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Button } from '@/components/ui/Button';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { postsApi, Post, PostMedia } from '@/services/posts';
import { useTimezone } from '@/contexts/TimezoneContext';
import { cn } from '@/utils/cn';
import { useDebounce } from '@/hooks/useDebounce';
import { RetryImage } from '@/components/ui';

/**
 * PostSelector Component
 *
 * Search and select a parent post for creating a variation.
 * Features:
 * - Server-side search with debouncing
 * - Media type filtering (All, Image, Video, Carousel, PDF)
 * - Thumbnail preview with media type badges
 * - Load more pagination
 */

const PAGE_SIZE = 20;

type MediaTypeFilter = 'all' | 'image' | 'video' | 'carousel' | 'pdf' | 'text';

interface PostSelectorProps {
  onSelect: (post: Post) => void;
  disabled?: boolean;
}

/**
 * Get media type info for display
 */
const getMediaTypeInfo = (postMedia: PostMedia[] | undefined): {
  type: MediaTypeFilter;
  icon: string;
  label: string;
  count: number;
} => {
  if (!postMedia || postMedia.length === 0) {
    return { type: 'text', icon: '📝', label: 'Text', count: 0 };
  }

  const count = postMedia.length;
  const firstMedia = postMedia[0];

  if (count > 1) {
    return { type: 'carousel', icon: '🎠', label: 'Carousel', count };
  }

  switch (firstMedia.type) {
    case 'VIDEO':
      return { type: 'video', icon: '▶', label: 'Video', count: 1 };
    case 'PDF':
    case 'DOCUMENT':
      return { type: 'pdf', icon: '📄', label: 'PDF', count: 1 };
    default:
      return { type: 'image', icon: '🖼', label: 'Image', count: 1 };
  }
};

/**
 * Filter posts by media type (client-side, after server filtering)
 */
const filterByMediaType = (posts: Post[], filter: MediaTypeFilter): Post[] => {
  if (filter === 'all') return posts;

  return posts.filter((post) => {
    const mediaInfo = getMediaTypeInfo(post.postMedia);
    return mediaInfo.type === filter;
  });
};

export const PostSelector = ({ onSelect, disabled = false }: PostSelectorProps) => {
  const { t } = useTranslation('common');
  const { formatDateOnly } = useTimezone();
  const [searchQuery, setSearchQuery] = useState('');
  const [mediaTypeFilter, setMediaTypeFilter] = useState<MediaTypeFilter>('all');
  const [loadedPosts, setLoadedPosts] = useState<Post[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  // Debounce search to avoid too many API calls
  const debouncedSearch = useDebounce(searchQuery, 300);

  // Fetch posts with server-side search and pagination
  const { data: queryData, isLoading, isFetching } = useQuery({
    queryKey: ['posts', 'for-variation', debouncedSearch, page],
    queryFn: async () => {
      const response = await postsApi.list({
        status: 'ALL',
        platforms: null,
        dateFrom: null,
        dateTo: null,
        search: debouncedSearch,
        page,
        limit: PAGE_SIZE,
        sortBy: 'newest',
      });

      return response;
    },
  });

  // Sync query data to local state for pagination accumulation
  useEffect(() => {
    if (queryData) {
      const newPosts = queryData.data || [];
      setHasMore(newPosts.length === PAGE_SIZE);

      if (page === 1) {
        setLoadedPosts(newPosts);
      } else {
        setLoadedPosts((prev) => {
          // Avoid duplicates when effect re-runs
          const existingIds = new Set(prev.map((p) => p.id));
          const uniqueNew = newPosts.filter((p: Post) => !existingIds.has(p.id));
          return [...prev, ...uniqueNew];
        });
      }
    }
  }, [queryData, page]);

  // Apply media type filter (client-side)
  const filteredPosts = filterByMediaType(loadedPosts, mediaTypeFilter);

  // Reset pagination when search changes
  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
    setPage(1);
    setLoadedPosts([]);
    setHasMore(true);
  }, []);

  // Load more posts
  const handleLoadMore = useCallback(() => {
    if (!isFetching && hasMore) {
      setPage((prev) => prev + 1);
    }
  }, [isFetching, hasMore]);

  // Truncate caption for preview
  const truncateCaption = (caption: string, maxLength = 100): string => {
    if (caption.length <= maxLength) return caption;
    return caption.substring(0, maxLength).trim() + '...';
  };

  // Media type filter options (plain text, matching Creator page pattern)
  const mediaTypeOptions = [
    { value: 'all', label: t('all_types', { defaultValue: 'All Types' }) },
    { value: 'image', label: t('image', { defaultValue: 'Image' }) },
    { value: 'video', label: t('video', { defaultValue: 'Video' }) },
    { value: 'carousel', label: t('carousel', { defaultValue: 'Carousel' }) },
    { value: 'pdf', label: 'PDF' },
    { value: 'text', label: t('text_only', { defaultValue: 'Text Only' }) },
  ];

  return (
    <div className="space-y-4">
      {/* Search & Filter Row */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('select_parent_post', { defaultValue: 'Select Parent Post' })}
        </label>
        <div className="flex gap-3">
          {/* Search Input */}
          <div className="flex-1">
            <Input
              type="search"
              placeholder={t('search_posts', { defaultValue: 'Search posts...' })}
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              disabled={disabled}
              className="w-full"
            />
          </div>
          {/* Media Type Filter */}
          <div className="w-40">
            <Select
              value={mediaTypeFilter}
              onValueChange={(value) => setMediaTypeFilter(value as MediaTypeFilter)}
              options={mediaTypeOptions}
              placeholder={t('all_types', { defaultValue: 'All Types' })}
            />
          </div>
        </div>
      </div>

      {/* Posts List */}
      <div className="border border-gray-200 rounded-lg overflow-hidden max-h-[400px] overflow-y-auto">
        {isLoading && page === 1 ? (
          <div className="flex items-center justify-center p-8">
            <div className="inline-block h-6 w-6 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent" />
          </div>
        ) : filteredPosts.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-8 text-center">
            <Icon name={ICONS.INFO} size="lg" color="text-gray-400" />
            <p className="mt-2 text-sm text-gray-500">
              {searchQuery || mediaTypeFilter !== 'all'
                ? t('no_posts_match_search', { defaultValue: 'No posts match your search' })
                : t('no_posts_available', { defaultValue: 'No posts available' })}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredPosts.map((post) => {
              const mediaInfo = getMediaTypeInfo(post.postMedia);
              const firstMedia = post.postMedia && post.postMedia.length > 0 ? post.postMedia[0] : null;

              return (
                <button
                  key={post.id}
                  type="button"
                  onClick={() => onSelect(post)}
                  disabled={disabled}
                  className={cn(
                    'w-full text-left p-3 hover:bg-gray-50 transition-colors duration-150',
                    'focus:outline-none focus:bg-gray-100',
                    'disabled:opacity-50 disabled:cursor-not-allowed'
                  )}
                >
                  <div className="flex items-start gap-3">
                    {/* Thumbnail */}
                    <div className="relative w-16 h-16 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                      {firstMedia ? (
                        <>
                          <RetryImage
                            src={firstMedia.thumbnailUrl || firstMedia.originalUrl || undefined}
                            alt=""
                            className={cn(
                              'w-full h-full',
                              firstMedia.type === 'VIDEO' || firstMedia.type === 'PDF'
                                ? 'object-contain bg-gray-900'
                                : 'object-cover'
                            )}
                          />
                          {/* Media type badge */}
                          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-1.5 py-1">
                            <div className="flex items-center justify-between text-white text-[10px] font-medium">
                              <span>{mediaInfo.icon}</span>
                              {mediaInfo.count > 1 && <span>+{mediaInfo.count - 1}</span>}
                            </div>
                          </div>
                        </>
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-2xl">
                          📝
                        </div>
                      )}
                    </div>

                    {/* Post Details */}
                    <div className="flex-1 min-w-0">
                      {/* Caption Preview */}
                      <p className="text-sm text-gray-900 line-clamp-2 mb-1">
                        {post.caption ? truncateCaption(post.caption) : (
                          <span className="text-gray-400 italic">{t('no_caption', { defaultValue: 'No caption' })}</span>
                        )}
                      </p>

                      {/* Metadata Row */}
                      <div className="flex items-center gap-2 flex-wrap">
                        {/* Media Type Badge */}
                        <span className={cn(
                          'px-2 py-0.5 text-xs font-medium rounded flex items-center gap-1',
                          mediaInfo.type === 'text' ? 'bg-gray-100 text-gray-600' : 'bg-soft-mint text-dark-teal',
                        )}>
                          {mediaInfo.type === 'video' && <Icon name={ICONS.VIDEO} size="xs" ariaHidden />}
                          {mediaInfo.type === 'pdf' && <Icon name={ICONS.FILE} size="xs" ariaHidden />}
                          {mediaInfo.type === 'carousel' && <Icon name={ICONS.IMAGE} size="xs" ariaHidden />}
                          {mediaInfo.type === 'image' && <Icon name={ICONS.IMAGE} size="xs" ariaHidden />}
                          {mediaInfo.type === 'text' && <Icon name={ICONS.FILE} size="xs" ariaHidden />}
                          {mediaInfo.label}
                          {mediaInfo.count > 1 && ` (${mediaInfo.count})`}
                        </span>

                        {/* Character count */}
                        {post.caption && (
                          <span className="text-xs text-gray-400">
                            {post.caption.length} {t('chars', { defaultValue: 'chars' })}
                          </span>
                        )}

                        {/* Labels */}
                        {post.labels && post.labels.length > 0 && (
                          <span className="text-xs text-gray-400">
                            · {post.labels.slice(0, 2).join(', ')}
                            {post.labels.length > 2 && ` +${post.labels.length - 2}`}
                          </span>
                        )}

                        {/* Variations Count */}
                        {post.variationsCount !== undefined && post.variationsCount > 0 && (
                          <span className="px-2 py-0.5 bg-soft-mint text-dark-teal text-xs font-medium rounded">
                            {post.variationsCount} {t('variations', { defaultValue: 'var' })}
                          </span>
                        )}

                        {/* Date */}
                        <span className="text-xs text-gray-400 ml-auto">
                          {formatDateOnly(new Date(post.createdAt))}
                        </span>
                      </div>
                    </div>

                    {/* Select Arrow */}
                    <div className="flex-shrink-0 self-center">
                      <Icon name={ICONS.ARROW_RIGHT} size="sm" color="text-primary-400" />
                    </div>
                  </div>
                </button>
              );
            })}

            {/* Load More Button */}
            {hasMore && loadedPosts.length > 0 && (
              <div className="p-4 bg-gray-50">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleLoadMore}
                  disabled={isFetching}
                  className="w-full"
                >
                  {isFetching ? (
                    <>
                      <div className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent mr-2" />
                      {t('loading', { defaultValue: 'Loading...' })}
                    </>
                  ) : (
                    t('load_more', { defaultValue: 'Load More' })
                  )}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Helper Text */}
      <p className="text-xs text-gray-500">
        {t('variation_parent_post_help', {
          defaultValue:
            'Select the post you want to create a variation for. Variations can have different captions, media, or labels.',
        })}
      </p>
    </div>
  );
};
